import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, Input, OnInit, signal } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';
import { Language } from '@app/shared/models';
import { LanguageFacade } from '@app/store/language';
import { AvatarBoxFullState } from '@app/store/audio/models/audio-state.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router, RouterLink } from '@angular/router';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { StripeFacade } from '@app/store/stripe';
import { UserSubscription } from '@app/core/api/stripe';
import { Observable } from 'rxjs';
import { AudioFacade } from '@app/store/audio';
import { UiFacade } from '@app/store/ui';
import { MatMenuModule } from '@angular/material/menu';
import { LowMinutesToastComponent } from '@app/components/low-minutes-toast/low-minutes-toast.component';
import { SharedModule } from '@app/shared/modules/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { UserFacade } from '@app/store/user/user.facade';
import { MatchingFacade } from '@app/store/matching/matching.facade';

@UntilDestroy()
@Component({
  selector: 'app-control-header',
  templateUrl: './control-header.component.html',
  styleUrl: './control-header.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MaterialSharedModule,
    AngularSvgIconModule,
    RouterLink,
    MatMenuModule,
    LowMinutesToastComponent,
    SharedModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ControlHeaderComponent implements OnInit {
  // Inputs to control what features are shown
  @Input() showAvatarControls = false; // Only true for home page
  
  // Additional control inputs
  @Input() hideMenuButton = false;
  @Input() disableStartStop = false;
  
  // State from NgRx store via facades
  conversationState$!: Observable<AvatarBoxFullState>;
  subscription$: Observable<UserSubscription | null>;
  subtitlesEnabled$!: Observable<boolean>;

  // Role-based visibility
  isManagerOrAdmin$!: Observable<boolean>;

  // Matching badge
  matchActionableCount$!: Observable<number>;

  // Language-related properties
  Language = Language;
  currentLanguage = signal(Language.EN);
  private translate = inject(TranslateService);
  languageOptions = signal<any[]>([]);

  constructor(
    private readonly audioFacade: AudioFacade,
    private readonly languageFacade: LanguageFacade,
    private readonly stripeFacade: StripeFacade,
    private readonly uiFacade: UiFacade,
    private readonly router: Router,
    private readonly userFacade: UserFacade,
    private readonly matchingFacade: MatchingFacade,
  ) {
    // Get current subscription with real-time updates from facade
    this.subscription$ = this.stripeFacade.currentSubscription$;
    this.isManagerOrAdmin$ = this.userFacade.isManagerOrAdmin$;
    this.matchActionableCount$ = this.matchingFacade.actionableCount$;
  }

  ngOnInit(): void {
    // Initialize state selectors from facades
    this.conversationState$ = this.audioFacade.avatarBoxFullState$;
    this.subtitlesEnabled$ = this.audioFacade.isSubtitleEnabled$;

    // Load matching data for badge counter
    this.matchingFacade.loadMatches();

    // Initialize language options
    this.updateLanguageOptions();
    
    // Update when translations change
    this.translate.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateLanguageOptions();
      });

    // Subscribe to language changes
    this.languageFacade.activeLanguage$
      .pipe(untilDestroyed(this))
      .subscribe((language: Language) => {
        this.currentLanguage.set(language);
      });
  }

  private updateLanguageOptions(): void {
    // Get options from facade and translate labels
    const options = this.languageFacade.getLanguageOptions();
    this.languageOptions.set(options.map(opt => ({
      ...opt,
      label: this.translate.instant(opt.label)
    })));
  }

  onMenuClick(): void {
    // Call facade directly - no need to bubble event to parent
    this.uiFacade.toggleSidenav();
  }

  onToggleSubtitles(): void {
    this.audioFacade.toggleSubtitles();
  }

  onStart(): void {
    // Call facade directly - no need to bubble event to parent
    this.audioFacade.startConversation();
  }

  onStop(): void {
    // Call facade directly - no need to bubble event to parent
    this.audioFacade.stopConversation();
  }

  onLanguageSelect(language: Language): void {
    this.currentLanguage.set(language);
    // Call facades directly - no need to bubble event to parent
    this.languageFacade.setActiveLanguage(language);
    this.languageFacade.updateUserPreferredLanguage(language);
  }

  getCurrentLanguageOption() {
    return this.languageOptions().find(option => option.value === this.currentLanguage()) || this.languageOptions()[0];
  }

  // Low minutes warning methods
  shouldShowLowMinutesWarning(subscription: UserSubscription): boolean {
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    return this.stripeFacade.shouldShowLowMinutesWarning(subscription, remaining);
  }

  getLowMinutesWarningText(subscription: UserSubscription): string {
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    return this.stripeFacade.getLowMinutesWarningText(remaining, subscription);
  }

  getStartButtonTooltip(state: any, subscription: UserSubscription | null): string {
    if (!subscription) {
      return this.translate.instant('tooltip.loadingSubscription');
    }
    
    if (subscription.is_expired) {
      return this.translate.instant('tooltip.subscriptionExpired');
    }
    
    if (!subscription.can_use_service) {
      return this.translate.instant('tooltip.noAccess');
    }
    
    // Check if user has active subscription or trial
    const hasActiveSubscription = subscription.has_subscription && subscription.is_active;
    const hasActiveTrial = subscription.is_trial && subscription.can_use_service;
    
    if (!hasActiveSubscription && !hasActiveTrial) {
      return this.translate.instant('tooltip.noActiveSubscription');
    }
    
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    if (remaining <= 0) {
      return this.translate.instant('tooltip.outOfMinutes');
    }
    
    if (!state.canStart) {
      return this.translate.instant('tooltip.pleaseWait');
    }
    
    return this.translate.instant('tooltip.startCall');
  }

  getRemainingMinutesText(subscription: UserSubscription): string {
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    return `${Math.ceil(remaining)} credits left`;
  }

  shouldShowTimeTracker(subscription: UserSubscription | null): boolean {
    if (!subscription) {
      return false;
    }
    
    // Show time tracker for users with active subscription or trial
    const hasActiveSubscription = subscription.has_subscription && subscription.is_active;
    const hasActiveTrial = subscription.is_trial && subscription.can_use_service;
    
    if (!hasActiveSubscription && !hasActiveTrial) {
      return false;
    }
    
    const remaining = this.stripeFacade.getRemainingMinutes(subscription);
    return remaining <= 5;
  }

  onGetMore(): void {
    this.router.navigate(['/billing']);
  }

  isHomePage(): boolean {
    return this.router.url === '/home';
  }
}
